import React, { FC, useState } from 'react';
import { captureException } from '@sentry/nextjs';
import { Box, Button, Text, useSnackbar } from '@sparknz/spark-pattern-library';

import { useSelectedPlans } from 'components/shop/MobilePlans/providers';
import { RelatedOfferDetails } from 'components/shop/MobilePlans/typings/plans';
import { pluralize } from 'components/shop/MobilePlans/utils/pluralize';
import { GENERIC_DEFAULT_ERROR_MESSAGE } from 'shared/constants/errorMessages.constants';
import PlanQuantitySelector from '../../../../../PlanQuantitySelector';

const MINIMUM_QUANTITY_TO_PROCEED = 1;

interface QuantitySelectorWithActionsProps {
  label: string;
  suffix: string;
  maxQuantity: number;
  minQuantity: number;
  isAddingBundles: boolean;
  isLoading: boolean;
  isLastQuantityStep: boolean;
  defaultQuantity: number;
  plan: RelatedOfferDetails;
  minimumQuantityToProceed?: number;
  onSkip(): void;
  onNext(): void;
}

const QuantitySelectorWithActions: FC<QuantitySelectorWithActionsProps> = ({
  label,
  suffix,
  plan,
  maxQuantity,
  minQuantity,
  isAddingBundles,
  defaultQuantity,
  isLastQuantityStep,
  minimumQuantityToProceed = MINIMUM_QUANTITY_TO_PROCEED,
  onSkip,
  onNext,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(defaultQuantity);

  const { selectIndividualPlan } = useSelectedPlans();
  const { showSnackbar } = useSnackbar();

  const { id, name } = plan;

  const handleReviewSelectionClick = async () => {
    try {
      if (!name) {
        throw new Error('Plan name is missing');
      }

      setIsLoading(true);

      await selectIndividualPlan(id, name, { quantity });

      onNext();
    } catch (error) {
      captureException(error);

      showSnackbar({
        text: GENERIC_DEFAULT_ERROR_MESSAGE,
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <>
      <Text variant="bodyMDemi" mb="s3">
        {label}
      </Text>
      <PlanQuantitySelector
        quantity={quantity}
        onIncrease={() => setQuantity((prev) => prev + 1)}
        onDecrease={() => setQuantity((prev) => prev - 1)}
        suffix={pluralize({ word: suffix, quantity })}
        minQuantity={minQuantity}
        maxQuantity={maxQuantity}
        disabled={isAddingBundles}
      />

      <Box flexDirection="column" gap="s2">
        <Button
          variant="primary"
          onClick={handleReviewSelectionClick}
          isLoading={isLoading}
          disabled={quantity < minimumQuantityToProceed || isAddingBundles}
          fullWidth
        >
          {isLastQuantityStep ? 'Review selection' : 'Continue'}
        </Button>
        <Button
          variant="default"
          onClick={onSkip}
          isLoading={isAddingBundles}
          fullWidth
        >
          Never mind, skip
        </Button>
      </Box>
    </>
  );
};

export default QuantitySelectorWithActions;
